// Selection = a set of node `path` prefixes. A bare prefix includes its subtree; a
// "!" prefix excludes one from an including ancestor; for any page the longest match
// wins. Mirrors `selected_urls` in the backend `url_tree.py`.

export interface UrlTreeNode {
  label: string;
  path: string;
  url: string | null;
  count: number;
  children: UrlTreeNode[];
  truncated: number;
}

const isExclude = (entry: string): boolean => entry.startsWith('!');
const stripExclude = (entry: string): string => (entry.startsWith('!') ? entry.slice(1) : entry);

// True when `descendant` lies strictly under `ancestor`; the empty-path root is the
// ancestor of every node (sibling hosts share no prefix, so only it can group them).
const isUnder = (descendant: string, ancestor: string): boolean =>
  ancestor === '' ? descendant !== '' : descendant.startsWith(`${ancestor}/`);

type Coverage = 'include' | 'exclude' | 'none';

// Sign of the longest prefix covering `path`; a same-length exclude beats an include.
const coverage = (path: string, selection: Set<string>): Coverage => {
  let bestLen = -1;
  let sign: Coverage = 'none';
  selection.forEach((entry) => {
    const prefix = stripExclude(entry);
    if (!(prefix === path || isUnder(path, prefix))) return;
    const excl = isExclude(entry);
    if (prefix.length > bestLen || (prefix.length === bestLen && excl)) {
      bestLen = prefix.length;
      sign = excl ? 'exclude' : 'include';
    }
  });
  return sign;
};

// Whether any include (or exclude) entry sits strictly under `path`.
const hasDescendant = (
  path: string,
  selection: Set<string>,
  sign: 'include' | 'exclude',
): boolean =>
  Array.from(selection).some((entry) => {
    const prefix = stripExclude(entry);
    return (sign === 'exclude') === isExclude(entry) && prefix !== path && isUnder(prefix, path);
  });

// Fully selected: covered by an include with nothing excluded beneath it.
export const isChecked = (path: string, selection: Set<string>): boolean =>
  coverage(path, selection) === 'include' && !hasDescendant(path, selection, 'exclude');

// Partially selected: covered by an include but with exclusions beneath, or not
// covered but with inclusions beneath.
export const isIndeterminate = (path: string, selection: Set<string>): boolean =>
  coverage(path, selection) === 'include'
    ? hasDescendant(path, selection, 'exclude')
    : hasDescendant(path, selection, 'include');

// Toggle the subtree at `path`, adding only the minimal marker against the nearest
// covering ancestor, so truncated / lazily-loaded children need no enumeration.
export const toggleSelection = (selection: Set<string>, path: string): Set<string> => {
  const next = new Set(selection);
  const turningOn = !isChecked(path, selection);
  // Drop any entries at or under `path`; we're redefining this subtree.
  Array.from(next).forEach((entry) => {
    const prefix = stripExclude(entry);
    if (prefix === path || isUnder(prefix, path)) next.delete(entry);
  });
  const ancestor = coverage(path, next); // nearest covering ancestor, post-clear
  if (turningOn) {
    if (ancestor !== 'include') next.add(path);
  } else if (ancestor === 'include') {
    next.add(`!${path}`);
  }
  return next;
};

// Graft lazily-loaded children onto the node at `path` (immutable, idempotent),
// shrinking `truncated` so "+N more" and `countSelected` see the loaded pages.
export const mergeChildren = (
  node: UrlTreeNode,
  path: string,
  batch: UrlTreeNode[],
): UrlTreeNode => {
  if (node.path === path) {
    const existing = new Set((node.children ?? []).map((child) => child.path));
    const fresh = batch.filter((child) => !existing.has(child.path));
    if (fresh.length === 0) return node;
    return {
      ...node,
      children: [...(node.children ?? []), ...fresh],
      truncated: Math.max(0, node.truncated - fresh.length),
    };
  }
  if (!isUnder(path, node.path)) return node;
  let changed = false;
  const children = (node.children ?? []).map((child) => {
    const merged = mergeChildren(child, path, batch);
    if (merged !== child) changed = true;
    return merged;
  });
  return changed ? { ...node, children } : node;
};

// Display domain for the tree root: the host (single-host) or seed-named label
// (multi-host), falling back to the child hosts' shared domain for legacy maps.
export const rootDomain = (node: UrlTreeNode): string => {
  if (node.path) return node.path;
  if (node.label) return node.label;
  const hosts = (node.children ?? []).map((child) => child.label).filter(Boolean);
  if (hosts.length === 0) return node.label;
  const reversed = hosts.map((host) => host.split('.').reverse());
  const [first] = reversed;
  let shared = 0;
  while (shared < first.length && reversed.every((labels) => labels[shared] === first[shared])) {
    shared += 1;
  }
  return shared === 0 ? hosts[0] : first.slice(0, shared).reverse().join('.');
};

// Pages the display caps hid under `node`: neither its own page nor inside a shown child.
const hiddenPageCount = (node: UrlTreeNode): number => {
  const shown = (node.children ?? []).reduce((sum, child) => sum + child.count, 0);
  return Math.max(0, node.count - shown - (node.url != null ? 1 : 0));
};

// Markers aimed at hidden pages, e.g. a lazily loaded child the refetched preview no longer shows.
const hiddenMarkers = (node: UrlTreeNode, selection: Set<string>): string[] =>
  Array.from(selection).filter((entry) => {
    const prefix = stripExclude(entry);
    return (
      isUnder(prefix, node.path) &&
      !(node.children ?? []).some((child) => prefix === child.path || isUnder(prefix, child.path))
    );
  });

// Hidden pages the selection covers; each marker aimed at one is taken to flip exactly one page.
const selectedHiddenPages = (
  node: UrlTreeNode,
  selection: Set<string>,
  nodeIncluded: boolean,
): number => {
  const hidden = hiddenPageCount(node);
  if (hidden === 0) return 0;
  let selected = nodeIncluded ? hidden : 0;
  new Set(hiddenMarkers(node, selection).map(stripExclude)).forEach((path) => {
    const included = coverage(path, selection) === 'include';
    if (included !== nodeIncluded) selected += included ? 1 : -1;
  });
  return Math.min(hidden, Math.max(0, selected));
};

const countPages = (
  node: UrlTreeNode,
  selection: Set<string>,
  inherited: boolean,
  hiddenSelected: (node: UrlTreeNode, included: boolean) => number,
): number => {
  let selected = inherited;
  if (selection.has(node.path)) selected = true;
  if (selection.has(`!${node.path}`)) selected = false;

  const hasDeeper = Array.from(selection).some((entry) => isUnder(stripExclude(entry), node.path));
  if (!hasDeeper) return selected ? node.count : 0;

  let sum = (node.children ?? []).reduce(
    (total, child) => total + countPages(child, selection, selected, hiddenSelected),
    0,
  );
  if (selected && node.url != null) sum += 1;
  return sum + hiddenSelected(node, selected);
};

// Pages the selection covers, honouring excludes; settles on a subtree's full `count`
// (which includes truncated descendants) so it stays correct on an incomplete tree.
// Hidden pages share their node's coverage: a marker on a path the tree doesn't show may not be in it at all.
export const countSelected = (node: UrlTreeNode, selection: Set<string>): number =>
  countPages(node, selection, false, (n, included) => (included ? hiddenPageCount(n) : 0));

// The review's own map: its only markers on hidden pages come from its lazy loads, so they count.
export const countReviewSelection = (node: UrlTreeNode, selection: Set<string>): number =>
  countPages(node, selection, false, (n, included) => selectedHiddenPages(n, selection, included));

// Clear the whole selection ("select none").
export const clearSelection = (): Set<string> => new Set<string>();

// Copy every marker under a subtree taken whole, reproducing the user's coverage exactly: carrying
// only the excludes would drop the re-includes they shadow, carrying none would resurrect deselections.
const carrySubtreeMarkers = (node: UrlTreeNode, current: Set<string>): string[] =>
  Array.from(current).filter((entry) => isUnder(stripExclude(entry), node.path));

// The marker that gives `path` the wanted coverage under what its ancestors already give it.
const ownMarker = (covered: boolean, include: boolean, path: string): string[] =>
  covered === include ? [] : [include ? path : `!${path}`];

type QueuedUnit = { node: UrlTreeNode; hidden: boolean };

// Keep at most `limit` already-selected pages, breadth-first; a unit (own page + hidden pages) that doesn't fit is skipped whole.
export const trimToLimit = (
  node: UrlTreeNode,
  limit: number,
  current: Set<string>,
): Set<string> => {
  const includedCache = new Map<UrlTreeNode, boolean>();
  const isIncluded = (n: UrlTreeNode): boolean => {
    let included = includedCache.get(n);
    if (included === undefined) {
      included = coverage(n.path, current) === 'include';
      includedCache.set(n, included);
    }
    return included;
  };
  const unitSize = (n: UrlTreeNode): number =>
    (isIncluded(n) && n.url != null ? 1 : 0) + selectedHiddenPages(n, current, isIncluded(n));

  const taken = new Set<UrlTreeNode>();
  let budget = limit;
  let level: QueuedUnit[] = [{ node, hidden: false }];
  while (level.length > 0 && budget > 0) {
    const next: QueuedUnit[] = [];
    level.forEach(({ node: n, hidden }) => {
      if (hidden || n.url != null) {
        const size = unitSize(n);
        if (size > 0 && size <= budget) {
          budget -= size;
          taken.add(n);
        }
      }
      if (hidden) return;
      (n.children ?? []).forEach((child) => next.push({ node: child, hidden: false }));
      // A page-less node's hidden pages queue one level down, after its shown children.
      if (n.url == null && hiddenPageCount(n) > 0) next.push({ node: n, hidden: true });
    });
    level = next;
  }

  const tallies = new Map<UrlTreeNode, { selected: number; kept: number }>();
  const tally = (n: UrlTreeNode): { selected: number; kept: number } => {
    const own = unitSize(n);
    const result = (n.children ?? []).reduce(
      (sum, child) => {
        const sub = tally(child);
        return { selected: sum.selected + sub.selected, kept: sum.kept + sub.kept };
      },
      { selected: own, kept: taken.has(n) ? own : 0 },
    );
    tallies.set(n, result);
    return result;
  };
  tally(node);

  // Markers leaving exactly the kept pages under `n`; memoised per `covered`, as a page-less group tries both.
  const plans = new Map<UrlTreeNode, Partial<Record<'covered' | 'uncovered', string[]>>>();
  const plan = (n: UrlTreeNode, covered: boolean): string[] => {
    const flag = covered ? 'covered' : 'uncovered';
    const cached = plans.get(n)?.[flag];
    if (cached) return cached;
    const { selected, kept } = tallies.get(n) ?? { selected: 0, kept: 0 };
    const childPlans = (include: boolean) =>
      (n.children ?? []).flatMap((child) => plan(child, include));
    let markers: string[];
    if (kept === 0) {
      markers = ownMarker(covered, false, n.path);
    } else if (kept === selected) {
      markers = [...ownMarker(covered, isIncluded(n), n.path), ...carrySubtreeMarkers(n, current)];
    } else if (n.url != null || hiddenPageCount(n) > 0) {
      // A kept unit keeps the user's own coverage, including their markers on hidden pages.
      const keepUnit = taken.has(n);
      const include = keepUnit && isIncluded(n);
      markers = [
        ...ownMarker(covered, include, n.path),
        ...(keepUnit ? hiddenMarkers(n, current) : []),
        ...childPlans(include),
      ];
    } else {
      const viaInclude = [...ownMarker(covered, true, n.path), ...childPlans(true)];
      const viaExclude = [...ownMarker(covered, false, n.path), ...childPlans(false)];
      const preferInclude =
        viaInclude.length < viaExclude.length ||
        (viaInclude.length === viaExclude.length && covered);
      markers = preferInclude ? viaInclude : viaExclude;
    }
    plans.set(n, { ...plans.get(n), [flag]: markers });
    return markers;
  };
  return new Set(plan(node, false));
};

export type SelectionSource = 'restored' | 'all' | 'fit' | 'resume_all';

export const seedSelection = (
  tree: UrlTreeNode,
  saved: string[],
  limit: number,
  resumeAll: boolean,
): { selection: Set<string>; source: SelectionSource } => {
  const all = new Set([tree.path]);
  const fits = tree.count <= limit;
  if (resumeAll && fits) return { selection: all, source: 'resume_all' };
  if (saved.length > 0) return { selection: new Set(saved), source: 'restored' };
  if (fits || !(limit > 0)) return { selection: all, source: 'all' };
  const fitted = trimToLimit(tree, limit, all);
  return countReviewSelection(tree, fitted) > 0
    ? { selection: fitted, source: 'fit' }
    : { selection: all, source: 'all' };
};
